// External libraries
import {
  genericValidationFn,
  isString,
  validatePassword,
} from '@checkout/react-components';

// Internal modules
import { getLocale, getUnifiedLoginContextData } from '../../lib/globals';
import { features } from '../../lib/features';
import { VALID_FIELD_STATE } from './constants';

const getDefaultKycStateOverride = field => {
  const { country } = getLocale();
  const defaultValues = {
    countryOfResidence: country,
    identityDocumentType: features.config.kycIdTypes[0],
    nationality: country,
    placeOfBirth: country,
    secondaryIdentityDocumentType: features.config.secondaryKycIdTypes[0],
  };
  const value = defaultValues[field];

  if (!value) {
    return {};
  }

  return {
    ...VALID_FIELD_STATE,
    value,
  };
};

const revalidatePassword = state => {
  const props = {
    countryCode: getLocale().country,
    user: {
      dateOfBirth: state.dateOfBirth?.value,
      firstName: state.firstName?.value,
      lastName: state.lastName?.value,
    },
  };

  return validatePassword(state.password.value, { props });
};

const wasInfoSecFieldChanged = fieldName => {
  return /^(firstName|lastName|dateOfBirth)$/.test(fieldName);
};

const getDefaultInputState = (overrides = {}) => ({
  displayError: false,
  error: '',
  errorCode: 'valueMissing',
  isDirty: false, // isDirty -> input that has had value change
  isValid: false,
  value: '',
  ...overrides,
});

/**
 * @function getDefaultEmailState
 * @returns {object}
 * @description Returns the default email state for the store. If
 * the user entered a valid email on UL then it will use that email
 * as the value.
 */
const getDefaultEmailState = () => {
  const { email } = getUnifiedLoginContextData();

  if (!isString(email?.stringValue)) {
    return getDefaultInputState();
  }

  const value = email.stringValue;
  const { isValid } = genericValidationFn(value, {
    props: { required: true, type: 'email' },
  });
  const overrides = isValid ? { errorCode: '', isValid, value } : {};

  return getDefaultInputState(overrides);
};

/**
 * @function getBaseAddressFields
 * @param {string} addressType
 * @returns {object}
 * @description Returns an object with shipping/billing state for
 * city, line1, line2, postcode, state
 */
const getBaseAddressFields = () => {
  const fields = [
    'city',
    'houseNumber',
    'line1',
    'line2',
    'postalCode',
    'state',
    'streetName',
  ];
  return fields.reduce((accum, field) => {
    accum[field] = getDefaultInputState();
    return accum;
  }, {});
};

const getAddressValidationPayload = ({ action, errorCode = '', fields }) => {
  const isSetError = action === 'setError';
  const initialState = {
    meta: { hasValidationError: isSetError },
  };

  return fields.reduce((accum, field) => {
    accum[field] = {
      displayError: isSetError,
      errorCode,
      isValid: !isSetError,
    };
    return accum;
  }, initialState);
};

export {
  getAddressValidationPayload,
  getBaseAddressFields,
  getDefaultEmailState,
  getDefaultInputState,
  getDefaultKycStateOverride,
  revalidatePassword,
  wasInfoSecFieldChanged,
};
